/**
 * ResetPasswordModal Component - Modal for resetting user passwords
 */
import React, { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { msg, MESSAGES } from '../../utils/messages';
import { validatePassword, getStrengthColor } from '../../services/passwordService';
import { User } from '../../types/user';

interface ResetPasswordModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onReset: (userId: number, newPassword: string) => Promise<{ success: boolean; password?: string; error?: string }>;
}

export const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  user,
  onClose,
  onReset,
}) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState<{ score: number; label: string }>({ score: 0, label: 'Fraca' });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setConfirmPassword('');
      setMessage(null);
      setGeneratedPassword(null);
      setPasswordStrength({ score: 0, label: 'Fraca' });
      setPasswordErrors([]);
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen]);

  const handlePasswordInput = (value: string) => {
    setPassword(value);
    setMessage(null);

    if (value.length > 0) {
      validatePassword(value, user?.id)
        .then(result => {
          setPasswordStrength({ score: result.strength_score, label: result.strength_label });
          setPasswordErrors(result.errors);
        })
        .catch(() => {
          setPasswordStrength({ score: 0, label: 'Fraca' });
          setPasswordErrors(['Erro ao validar senha']);
        });
    } else {
      setPasswordStrength({ score: 0, label: 'Fraca' });
      setPasswordErrors([]);
    }
  };

  const handleReset = async () => {
    if (!user) return;

    setMessage(null);

    // Validation
    if (!password || !confirmPassword) {
      setMessage({ type: 'error', text: 'Preencha todos os campos' });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'As senhas não coincidem' });
      return;
    }

    if (password.length < 8) {
      setMessage({ type: 'error', text: 'A senha deve ter no mínimo 8 caracteres' });
      return;
    }

    setResetting(true);

    const result = await onReset(user.id, password);

    setResetting(false);

    if (result.success) {
      setGeneratedPassword(result.password || password);
      setMessage({
        type: 'success',
        text: `Senha redefinida! Copie a senha abaixo para comunicar ao usuário ${user.email}`
      });
      setPassword('');
      setConfirmPassword('');
    } else {
      setMessage({ type: 'error', text: result.error || 'Erro ao resetar senha' });
    }
  };

  const handleCopyPassword = () => {
    if (generatedPassword) {
      navigator.clipboard.writeText(generatedPassword);
      msg.success(MESSAGES.USER.PASSWORD_COPIED);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-[600px] w-[90%] max-h-[90vh] overflow-y-auto shadow-2xl">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Redefinir Senha do Usuário
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-6">
          Digite a nova senha para o usuário: {user.email}
        </p>

        {/* Success/Error Message */}
        {message && (
          <div className={`p-4 rounded-lg mb-4 ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
            <p className={`text-sm ${message.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
              {message.text}
            </p>
          </div>
        )}

        {/* Show generated password after success */}
        {generatedPassword && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Senha redefinida com sucesso! Copie a senha abaixo:
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={generatedPassword}
                readOnly
                className="flex-1 px-3.5 py-2.5 border border-blue-300 dark:border-blue-700 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
              />
              <button
                onClick={handleCopyPassword}
                className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                title="Copiar senha"
              >
                Copiar
              </button>
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
              Comunique esta senha ao usuário de forma segura
            </p>
          </div>
        )}

        {/* Password form (hide after success) */}
        {!generatedPassword && (
          <div className="grid gap-4">
            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => handlePasswordInput(e.target.value)}
                  placeholder="Digite a nova senha (mínimo 8 caracteres)"
                  className="w-full px-3.5 py-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-gray-400">Força da senha:</span>
                    <span style={{ color: getStrengthColor(passwordStrength.score) }} className="font-semibold">
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${passwordStrength.score}%`,
                        backgroundColor: getStrengthColor(passwordStrength.score)
                      }}
                    />
                  </div>
                  {passwordErrors.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {passwordErrors.map((error, idx) => (
                        <li key={idx} className="text-xs text-red-600 dark:text-red-400">• {error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                A senha deve ter entre 8 e 64 caracteres
              </p>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5 block">
                Confirmar Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setMessage(null); }}
                  placeholder="Digite novamente a senha"
                  className="w-full px-3.5 py-2.5 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 mt-8 justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-semibold transition-colors"
          >
            {generatedPassword ? 'Fechar' : 'Cancelar'}
          </button>
          {!generatedPassword && (
            <button
              onClick={handleReset}
              disabled={resetting}
              className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {resetting ? 'Redefinindo...' : 'Redefinir Senha'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
